/**
 * EditPermanentModal — édition complète de la fiche d'un permanent
 * indépendant. Corrige les erreurs de saisie (civilité, prénom, nom,
 * adresse, SIRET, etc.) SANS toucher aux contrats déjà émis.
 *
 * Après enregistrement, l'admin peut re-générer les contrats DRAFT
 * (bouton "Régénérer le PDF" sur la timeline) pour que les modifs
 * apparaissent dans le PDF légal.
 */
import { useState } from "react";
import { X, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const REGIONS = [
    { value: "idf", label: "Île-de-France (IDF)" },
    { value: "grand-est", label: "Grand Est" },
    { value: "alsace", label: "Alsace" },
    { value: "hauts-de-france", label: "Hauts-de-France" },
    { value: "normandie", label: "Normandie" },
    { value: "bretagne", label: "Bretagne" },
    { value: "pays-de-la-loire", label: "Pays de la Loire" },
    { value: "centre-val-de-loire", label: "Centre-Val de Loire" },
    { value: "bourgogne-franche-comte", label: "Bourgogne-Franche-Comté" },
    { value: "auvergne-rhone-alpes", label: "Auvergne-Rhône-Alpes" },
    { value: "provence-alpes-cote-dazur", label: "Provence-Alpes-Côte d'Azur" },
    { value: "occitanie", label: "Occitanie" },
    { value: "nouvelle-aquitaine", label: "Nouvelle-Aquitaine" },
    { value: "corse", label: "Corse" },
    { value: "outre-mer", label: "Outre-mer" },
];


export default function EditPermanentModal({ pid, permanent, onClose, onDone }) {
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({
        civilite: permanent.civilite || "Monsieur",
        first_name: permanent.first_name || "",
        last_name: permanent.last_name || "",
        email: permanent.email || "",
        phone: permanent.phone || "",
        address_line1: permanent.address_line1 || "",
        address_postal_code: permanent.address_postal_code || "",
        address_city: permanent.address_city || "",
        birth_date: permanent.birth_date || "",
        birth_city: permanent.birth_city || "",
        nationality: permanent.nationality || "",
        siret: permanent.siret || "",
        region: permanent.region || "idf",
        legal_status: permanent.legal_status || "autoentrepreneur",
    });

    function setField(k, v) {
        setForm(prev => ({ ...prev, [k]: v }));
    }

    async function onSave() {
        setSaving(true);
        try {
            // Nettoyage : n'envoyer que les champs différents ou remplis.
            const payload = {};
            for (const [k, v] of Object.entries(form)) {
                if (v !== permanent[k] && v !== "") payload[k] = v;
            }
            if (Object.keys(payload).length === 0) {
                toast.info("Aucune modification à enregistrer");
                setSaving(false);
                return;
            }
            await api.patch(`/permanents/${pid}`, payload);
            toast.success(
                "Fiche mise à jour · si un contrat DRAFT existe, cliquez « Régénérer le PDF » pour le rafraîchir"
            );
            onDone();
        } catch (e) {
            toast.error("Enregistrement échoué : " + (e?.response?.data?.detail || e.message));
        } finally {
            setSaving(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto"
            onClick={onClose}
            data-testid="permanent-edit-modal"
        >
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-8 max-h-[90vh] overflow-y-auto"
                onClick={e => e.stopPropagation()}
            >
                <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] font-black uppercase tracking-widest text-shariti-orange">
                            Édition fiche permanent
                        </div>
                        <h2 className="text-lg font-black text-shariti-black mt-0.5">
                            {permanent.first_name} {permanent.last_name}
                        </h2>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="permanent-edit-close"
                        className="p-1.5 rounded-full hover:bg-slate-100 transition"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                <div className="p-6 space-y-5">
                    {/* Civilité + noms */}
                    <div>
                        <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5">
                            Civilité *
                        </label>
                        <div className="flex gap-2" data-testid="permanent-edit-civilite">
                            {["Madame", "Monsieur"].map(v => (
                                <button
                                    key={v}
                                    type="button"
                                    onClick={() => setField("civilite", v)}
                                    data-testid={`permanent-edit-civilite-${v.toLowerCase()}`}
                                    className={`flex-1 px-4 py-2.5 rounded-xl border-2 text-sm font-black uppercase tracking-wider transition ${form.civilite === v
                                        ? "border-shariti-orange bg-shariti-orange-soft text-shariti-orange"
                                        : "border-slate-200 bg-white text-slate-500 hover:border-slate-300"}`}
                                >
                                    {v}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Prénom (légal)" value={form.first_name} onChange={v => setField("first_name", v)} testid="permanent-edit-first-name" required />
                        <Field label="Nom (légal)" value={form.last_name} onChange={v => setField("last_name", v)} testid="permanent-edit-last-name" required />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Email" value={form.email} onChange={v => setField("email", v)} testid="permanent-edit-email" type="email" required />
                        <Field label="Téléphone" value={form.phone} onChange={v => setField("phone", v)} testid="permanent-edit-phone" />
                    </div>

                    <div className="border-t border-slate-100 pt-4">
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Adresse</div>
                        <Field label="Rue" value={form.address_line1} onChange={v => setField("address_line1", v)} testid="permanent-edit-address" />
                        <div className="grid grid-cols-3 gap-3 mt-3">
                            <Field label="Code postal" value={form.address_postal_code} onChange={v => setField("address_postal_code", v)} testid="permanent-edit-postal" />
                            <div className="col-span-2">
                                <Field label="Ville" value={form.address_city} onChange={v => setField("address_city", v)} testid="permanent-edit-city" />
                            </div>
                        </div>
                    </div>

                    <div className="border-t border-slate-100 pt-4">
                        <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-2">Informations légales</div>
                        <div className="grid grid-cols-2 gap-3">
                            <Field label="SIRET (14 chiffres)" value={form.siret} onChange={v => setField("siret", v.replace(/\D/g, "").slice(0, 14))} testid="permanent-edit-siret" mono />
                            <div>
                                <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                                    Région
                                </label>
                                <select
                                    value={form.region}
                                    onChange={e => setField("region", e.target.value)}
                                    data-testid="permanent-edit-region"
                                    className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-shariti-orange"
                                >
                                    {REGIONS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                                </select>
                            </div>
                        </div>
                        <div className="grid grid-cols-3 gap-3 mt-3">
                            <Field label="Date de naissance" value={form.birth_date} onChange={v => setField("birth_date", v)} testid="permanent-edit-birth-date" type="date" />
                            <Field label="Ville de naissance" value={form.birth_city} onChange={v => setField("birth_city", v)} testid="permanent-edit-birth-city" />
                            <Field label="Nationalité" value={form.nationality} onChange={v => setField("nationality", v)} testid="permanent-edit-nationality" />
                        </div>
                    </div>

                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-800">
                        <strong>Note</strong> : les contrats déjà signés ne changeront pas. Les contrats DRAFT
                        auront un bouton <em>« Régénérer le PDF »</em> pour appliquer les modifications.
                    </div>
                </div>

                <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="permanent-edit-cancel"
                        className="px-4 py-2 rounded-full border border-slate-200 text-slate-700 text-sm font-black uppercase tracking-widest hover:bg-slate-50 transition"
                    >
                        Annuler
                    </button>
                    <button
                        type="button"
                        onClick={onSave}
                        disabled={saving}
                        data-testid="permanent-edit-save"
                        className="px-4 py-2 rounded-full bg-shariti-orange hover:bg-shariti-orange/90 disabled:opacity-40 text-white text-sm font-black uppercase tracking-widest transition inline-flex items-center gap-2"
                    >
                        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                        Enregistrer
                    </button>
                </div>
            </div>
        </div>
    );
}


function Field({ label, value, onChange, testid, type = "text", required = false, mono = false }) {
    return (
        <div>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                {label}{required && <span className="text-red-500 ml-0.5">*</span>}
            </label>
            <input
                type={type}
                value={value || ""}
                onChange={e => onChange(e.target.value)}
                data-testid={testid}
                className={`w-full px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm focus:outline-none focus:border-shariti-orange ${mono ? "font-mono" : ""}`}
            />
        </div>
    );
}
